// ========================================
// pages/PrivacyPolicy.js - Privacy Policy
// ========================================
// PROJECT: Agyapadie Vault - Digital Inheritance Platform
// LAST MODIFIED: April 11, 2026
// 
// PURPOSE: Legal document explaining how we collect, use, and protect user data
// 
// ASSETS USED (from your inventory):
// - /images/symbols/golden-stool.png - Hero badge icon
// 
// SECTIONS:
// - Hero with title and last updated date
// - Information We Collect
// - How We Use Your Information
// - Data Protection & Security
// - Data Retention
// - Your Rights
// - Cookies & Tracking
// - Third-Party Services
// - Children's Privacy
// - International Data Transfers
// - Changes to This Policy
// - Contact Information
// 
// RESPONSIVE BREAKPOINTS:
// - Desktop: > 1024px
// - Tablet: 768px - 1024px
// - Mobile: < 768px
// ========================================

import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  const lastUpdated = 'April 11, 2026';

  return (
    <div className="privacy-page">
      
      {/* ======================================== */}
      {/* SECTION 1: HERO */}
      {/* ======================================== */}
      <section className="privacy-hero">
        <div className="privacy-hero-overlay"></div>
        <div className="privacy-container">
          <div className="privacy-hero-content">
            <div className="privacy-badge">
              <img src="/images/symbols/golden-stool.png" alt="Golden Stool" className="privacy-badge-icon" />
              <span>Legal Document</span>
            </div>
            <h1 className="privacy-hero-title">
              Privacy <span className="gold">Policy</span>
            </h1>
            <p className="privacy-hero-subtitle">
              Last Updated: <strong>{lastUpdated}</strong>
            </p>
            <p className="privacy-hero-description">
              At Agyapadie Vault, your privacy is our priority. This policy explains how we collect, 
              use, and protect your personal information.
            </p>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 2: POLICY CONTENT */}
      {/* ======================================== */}
      <section className="privacy-content">
        <div className="privacy-container">
          
          <div className="privacy-card">
            
            {/* Information We Collect */}
            <div className="privacy-section">
              <h2 className="privacy-section-title">1. Information We Collect</h2>
              <p>We collect the following types of information to provide and improve our services:</p>
              
              <div className="privacy-subsection">
                <h3>1.1 Personal Information You Provide</h3>
                <ul>
                  <li><strong>Account Information:</strong> Full name, email address, phone number, date of birth</li>
                  <li><strong>Asset Information:</strong> Bank account details, property information, vehicle details, investment data, digital asset information</li>
                  <li><strong>Beneficiary Information:</strong> Names and contact details of your Next of Kin, Trustees, and External Beneficiaries</li>
                  <li><strong>Communication Data:</strong> Messages sent through our contact forms or support channels</li>
                </ul>
              </div>
              
              <div className="privacy-subsection">
                <h3>1.2 Information Automatically Collected</h3>
                <ul>
                  <li><strong>Usage Data:</strong> How you interact with our platform (pages visited, features used, time spent)</li>
                  <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers</li>
                  <li><strong>Location Data:</strong> Approximate location based on IP address</li>
                </ul>
              </div>
              
              <div className="privacy-note">
                <strong>Note:</strong> We do NOT collect government ID documents (Ghana Card, Passport, etc.). You remain semi-anonymous on our platform.
              </div>
            </div>

            {/* How We Use Your Information */}
            <div className="privacy-section">
              <h2 className="privacy-section-title">2. How We Use Your Information</h2>
              <p>We use your information for the following purposes:</p>
              <ul>
                <li><strong>Provide Services:</strong> To create and manage your account, store your assets, and facilitate inheritance transfers</li>
                <li><strong>Death Verification:</strong> To contact trustees and verify death status as per your instructions</li>
                <li><strong>Beneficiary Notification:</strong> To notify beneficiaries about their inheritance</li>
                <li><strong>Security:</strong> To protect against fraud, unauthorized access, and maintain platform integrity</li>
                <li><strong>Communication:</strong> To send important updates, security alerts, and respond to support requests</li>
                <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations (Data Protection Act 843, GDPR, etc.)</li>
                <li><strong>Improvement:</strong> To analyze usage patterns and improve our services</li>
              </ul>
            </div>

            {/* Data Protection & Security */}
            <div className="privacy-section">
              <h2 className="privacy-section-title">3. Data Protection & Security</h2>
              <p>We implement multiple layers of security to protect your data:</p>
              <ul>
                <li><strong>Encryption:</strong> AES-256 encryption for data at rest, TLS 1.3 for data in transit</li>
                <li><strong>Access Controls:</strong> Strict role-based access controls; only authorized personnel can access your data</li>
                <li><strong>Audit Logs:</strong> All access to sensitive data is logged and monitored</li>
                <li><strong>Regular Testing:</strong> Quarterly penetration testing and vulnerability assessments</li>
                <li><strong>Data Minimization:</strong> We only collect data necessary for our services</li>
              </ul>
              <div className="privacy-note">
                <strong>Important:</strong> While we implement industry-standard security measures, no system is 100% secure. We encourage you to use strong passwords and enable security features.
              </div>
            </div>

            {/* Data Retention */}
            <div className="privacy-section">
              <h2 className="privacy-section-title">4. Data Retention</h2>
              <p>We retain your data for the following periods:</p>
              <ul>
                <li><strong>Active Accounts:</strong> Data retained while your account is active</li>
                <li><strong>Deleted Accounts:</strong> Data is deleted within 30 days of account deletion request</li>
                <li><strong>Inheritance Records:</strong> Permanent retention for legal and inheritance purposes (as they are legal records)</li>
                <li><strong>Asset Certificates:</strong> Permanent retention to maintain cryptographic proof integrity</li>
                <li><strong>Unclaimed Assets:</strong> Retained indefinitely until claimed or legally resolved</li>
              </ul>
            </div>

            {/* Your Rights */}
            <div className="privacy-section">
              <h2 className="privacy-section-title">5. Your Rights</h2>
              <p>Under the Data Protection Act 843 (Ghana) and GDPR (for EU users), you have the following rights:</p>
              <ul>
                <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
                <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your data (subject to legal retention requirements)</li>
                <li><strong>Right to Restrict Processing:</strong> Limit how we use your data</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a machine-readable format</li>
                <li><strong>Right to Object:</strong> Object to certain data processing activities</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
                <li><strong>Right to Lodge a Complaint:</strong> Complain to the Data Protection Commission (Ghana) or your local supervisory authority</li>
              </ul>
              <p>To exercise these rights, contact our Data Protection Officer at <a href="mailto:dpo@agyapadie.com">dpo@agyapadie.com</a>.</p>
            </div>

            {/* Cookies & Tracking */}
            <div className="privacy-section">
              <h2 className="privacy-section-title">6. Cookies & Tracking</h2>
              <p>We use cookies and similar technologies to:</p>
              <ul>
                <li><strong>Essential Cookies:</strong> Required for platform functionality (authentication, security)</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                <li><strong>Analytics Cookies:</strong> Understand how users interact with our platform (anonymized)</li>
              </ul>
              <p>You can control cookie settings through your browser preferences. Disabling essential cookies may affect platform functionality.</p>
            </div>

            {/* Third-Party Services */}
            <div className="privacy-section">
              <h2 className="privacy-section-title">7. Third-Party Services</h2>
              <p>We use the following third-party services that may process your data:</p>
              <ul>
                <li><strong>Hosting & Infrastructure:</strong> MongoDB Atlas, Cloud Providers (AWS/GCP/Azure)</li>
                <li><strong>Email Service:</strong> For sending verification and notification emails</li>
                <li><strong>Analytics:</strong> To understand platform usage (anonymized data only)</li>
              </ul>
              <p>All third-party providers are vetted for security and compliance (SOC 2, ISO 27001, GDPR compliant).</p>
            </div>

            {/* Children's Privacy */}
            <div className="privacy-section">
              <h2 className="privacy-section-title">8. Children's Privacy</h2>
              <p>
                Agyapadie Vault is not intended for individuals under 18 years of age. We do not knowingly 
                collect personal information from minors. If you believe a minor has provided us with 
                personal information, please contact us immediately.
              </p>
            </div>

            {/* International Data Transfers */}
            <div className="privacy-section">
              <h2 className="privacy-section-title">9. International Data Transfers</h2>
              <p>
                Your data may be transferred to and processed in countries outside your residence. 
                We ensure appropriate safeguards are in place, including:
              </p>
              <ul>
                <li>Standard Contractual Clauses (SCCs) for EU data transfers</li>
                <li>Data Processing Agreements with all third-party processors</li>
                <li>Compliance with GDPR and Data Protection Act 843 requirements</li>
              </ul>
            </div>

            {/* Changes to This Policy */}
            <div className="privacy-section">
              <h2 className="privacy-section-title">10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Material changes will be notified 
                via email or through a notice on our platform. The "Last Updated" date at the top of 
                this policy indicates when changes were made.
              </p>
            </div>

            {/* Contact Information */}
            <div className="privacy-section">
              <h2 className="privacy-section-title">11. Contact Information</h2>
              <p>For privacy-related questions or to exercise your rights, contact us at:</p>
              <div className="privacy-contact">
                <p><strong>Data Protection Officer:</strong> Abena Osei</p>
                <p><strong>Email:</strong> <a href="mailto:dpo@agyapadie.com">dpo@agyapadie.com</a></p>
                <p><strong>Address:</strong> 123 Independence Avenue, Ridge, Accra, Ghana</p>
                <p><strong>Phone:</strong> +233 (0) 30 212 3456</p>
              </div>
              <div className="privacy-note">
                <strong>Data Protection Commission (Ghana):</strong> For complaints, you may contact the Data Protection Commission at <a href="https://www.dataprotection.org.gh" target="_blank" rel="noopener noreferrer">www.dataprotection.org.gh</a>
              </div>
            </div>

          </div>
          
          {/* Back to Home Link */}
          <div className="privacy-back">
            <Link to="/" className="privacy-back-link">← Back to Home</Link>
          </div>
          
        </div>
      </section>

      {/* ======================================== */}
      {/* STYLES - Fully Responsive */}
      {/* ======================================== */}
      <style dangerouslySetInnerHTML={{ __html: `
        
        .privacy-page {
          font-family: 'Montserrat', sans-serif;
          background: #F5F0E6;
          min-height: 100vh;
        }
        
        .privacy-container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 0 20px;
        }
        
        .gold {
          color: #FFD700;
        }
        
        /* Hero Section */
        .privacy-hero {
          background: #0A0A0A;
          position: relative;
          padding: 80px 0;
          text-align: center;
        }
        
        .privacy-hero-overlay {
          position: absolute;
          inset: 0;
          background: url('/images/patterns/hero-pattern.png');
          opacity: 0.05;
          pointer-events: none;
        }
        
        .privacy-hero-content {
          position: relative;
          z-index: 2;
        }
        
        .privacy-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(255,215,0,0.1);
          padding: 6px 16px;
          border-radius: 30px;
          margin-bottom: 24px;
        }
        
        .privacy-badge-icon {
          height: 20px;
          width: auto;
        }
        
        .privacy-badge span {
          color: #FFD700;
          font-size: 14px;
        }
        
        .privacy-hero-title {
          font-size: 52px;
          font-weight: bold;
          color: white;
          margin-bottom: 15px;
        }
        
        .privacy-hero-subtitle {
          font-size: 16px;
          color: #FFD700;
          margin-bottom: 20px;
        }
        
        .privacy-hero-description {
          font-size: 18px;
          color: #ccc;
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.6;
        }
        
        /* Content Section */
        .privacy-content {
          padding: 60px 0;
        }
        
        .privacy-card {
          background: white;
          border-radius: 15px;
          padding: 50px;
          box-shadow: 0 5px 20px rgba(0,0,0,0.05);
        }
        
        .privacy-section {
          margin-bottom: 40px;
          padding-bottom: 30px;
          border-bottom: 1px solid #eee;
        }
        
        .privacy-section:last-child {
          border-bottom: none;
          margin-bottom: 0;
          padding-bottom: 0;
        }
        
        .privacy-section-title {
          font-size: 24px;
          color: #0A0A0A;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #FFD700;
          display: inline-block;
        }
        
        .privacy-subsection {
          margin-top: 20px;
          margin-left: 20px;
        }
        
        .privacy-subsection h3 {
          font-size: 18px;
          color: #2E7D32;
          margin-bottom: 10px;
        }
        
        .privacy-section p {
          color: #444;
          line-height: 1.7;
          margin-bottom: 15px;
        }
        
        .privacy-section ul {
          margin: 15px 0 15px 30px;
        }
        
        .privacy-section li {
          color: #555;
          line-height: 1.7;
          margin-bottom: 8px;
        }
        
        .privacy-note {
          background: #F5F0E6;
          padding: 15px 20px;
          border-radius: 10px;
          margin: 20px 0;
          border-left: 4px solid #FFD700;
          font-size: 14px;
        }
        
        .privacy-contact {
          background: #F5F0E6;
          padding: 20px;
          border-radius: 10px;
          margin-top: 15px;
        }
        
        .privacy-contact p {
          margin-bottom: 8px;
        }
        
        .privacy-contact a {
          color: #FFD700;
          text-decoration: none;
        }
        
        .privacy-contact a:hover {
          text-decoration: underline;
        }
        
        .privacy-section a {
          color: #FFD700;
          text-decoration: none;
        }
        
        .privacy-section a:hover {
          text-decoration: underline;
        }
        
        .privacy-back {
          text-align: center;
          margin-top: 40px;
        }
        
        .privacy-back-link {
          display: inline-block;
          color: #2E7D32;
          text-decoration: none;
          font-size: 14px;
          transition: color 0.3s;
        }
        
        .privacy-back-link:hover {
          color: #FFD700;
        }
        
        /* ======================================== */
        /* RESPONSIVE BREAKPOINTS */
        /* ======================================== */
        
        /* Tablet: 1024px and below */
        @media (max-width: 1024px) {
          .privacy-hero-title {
            font-size: 40px;
          }
          
          .privacy-card {
            padding: 40px;
          }
        }
        
        /* Mobile: 768px and below */
        @media (max-width: 768px) {
          .privacy-hero-title {
            font-size: 32px;
          }
          
          .privacy-hero-description {
            font-size: 16px;
          }
          
          .privacy-card {
            padding: 25px;
          }
          
          .privacy-section-title {
            font-size: 20px;
          }
          
          .privacy-subsection {
            margin-left: 10px;
          }
          
          .privacy-subsection h3 {
            font-size: 16px;
          }
          
          .privacy-section p,
          .privacy-section li {
            font-size: 14px;
          }
          
          .privacy-section ul {
            margin-left: 20px;
          }
        }
        
        /* Small Mobile: 480px and below */
        @media (max-width: 480px) {
          .privacy-hero-title {
            font-size: 28px;
          }
          
          .privacy-hero-description {
            font-size: 14px;
          }
          
          .privacy-card {
            padding: 20px;
          }
          
          .privacy-section-title {
            font-size: 18px;
          }
        }
        
      `}} />
      
    </div>
  );
};

export default PrivacyPolicy;